import {
    Component,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from "@angular/core";
import { filter, Subscription, take } from "rxjs";

import { Coordinates } from "@hermes/api-model-common";

import { LoadGeoLibrariesServiceModule } from "@hermes/map-utils";

import { MarkerData } from "../../map.type";
import { getBMapIcon, getBMapMarker } from "../helpers/bmap.helper";
import { MapBaiduService } from "../services/map-baidu.service";

type MouseEvent = "click" | "onmouseover" | "onmouseout";

export interface EventAndCoords {
    event: MouseEvent;
    coords: Coordinates;
}

export const MAX_ZOOM = 19;
export const DEFAULT_INCREMENT_ZOOM_LEVEL = 2;

@Component({
    selector: "h-map-baidu-marker",
    template: "",
    imports: [LoadGeoLibrariesServiceModule],
})
export class MapBaiduMarkerComponent implements OnInit, OnDestroy, OnChanges {
    @Input() public markers!: MarkerData[];

    @Input() public isInMobile: boolean = false;

    @Input() public isZoomOnClickActive: boolean = false;

    @Input() public isMarkerClustererActive: boolean = false;

    @Output()
    public hoveredMarkerCoordinates: EventEmitter<Coordinates> =
        new EventEmitter<Coordinates>();

    @Output()
    public unhoveredMarkerCoordinates: EventEmitter<Coordinates> =
        new EventEmitter<Coordinates>();

    @Output()
    public clickedMarkerCoordinates: EventEmitter<Coordinates> =
        new EventEmitter<Coordinates>();

    public subscription: Subscription = new Subscription();

    public bmapMarkers: BMap.Marker[] = [];

    constructor(
        private mapBaiduService: MapBaiduService,
        private ngZone: NgZone,
    ) {}

    public ngOnChanges(changes: SimpleChanges): void {
        if (
            changes["markers"] &&
            changes["markers"].previousValue !== undefined
        ) {
            /**
             * When the list of markers changes we want to avoid re-generating everything one by one.
             *
             * The factor of change today is ONLY when the icon that changes.
             *
             * So we analyze the markers that have a different URL and set the new icon.
             */
            const markers: typeof this.markers =
                changes["markers"].currentValue;

            markers.forEach((marker, index) => {
                if (
                    marker.icon.url !==
                    (this.bmapMarkers[index].getIcon()
                        .imageUrl as unknown as string)
                ) {
                    this.bmapMarkers[index].setIcon(getBMapIcon(marker.icon));
                }
            });
        }
    }

    public ngOnInit(): void {
        this.markers.forEach((marker) => {
            /**
             * Generate and add the BMap.Marker in the map :
             */
            const bmapMarker = getBMapMarker(marker);
            this.bmapMarkers.push(bmapMarker);
            this.mapBaiduService.addMarker(bmapMarker);

            /**
             * Added event listener to capture clicks and mouse over/out :
             */
            bmapMarker.addEventListener("click", () => {
                this.ngZone.run(() => {
                    this.manageClick(marker, bmapMarker);
                });
            });
            bmapMarker.addEventListener("onmouseover", () => {
                if (this.isInMobile) {
                    return;
                }

                this.ngZone.run(() => {
                    this.hoveredMarkerCoordinates.emit(marker.coordinates);
                });
            });
            bmapMarker.addEventListener("onmouseout", () => {
                if (this.isInMobile) {
                    return;
                }
                this.ngZone.run(() => {
                    this.unhoveredMarkerCoordinates.emit(marker.coordinates);
                });
            });
        });

        // Now that the markers have been added to the map, we can create the Clusterer
        if (this.isMarkerClustererActive) {
            this.mapBaiduService.loadAndCreateMarkerClusterer();
        }
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public manageClick(marker: MarkerData, bmapMarker: BMap.Marker): void {
        if (this.isZoomOnClickActive) {
            this.manageZoom(bmapMarker);
        }

        if (this.isInMobile) {
            this.hoveredMarkerCoordinates.emit(marker.coordinates);
            return;
        }
        this.clickedMarkerCoordinates.emit(marker.coordinates);
    }

    public manageZoom(bmapMarker: BMap.Marker): void {
        this.subscription.add(
            this.mapBaiduService
                .getZoom()
                .pipe(
                    filter((zoomLevel) => zoomLevel < MAX_ZOOM),
                    filter(() => !!bmapMarker),
                    take(1),
                )
                .subscribe((zoomLevel) => {
                    this.mapBaiduService.setCenterAndZoom(
                        bmapMarker.getPosition(),
                        zoomLevel + DEFAULT_INCREMENT_ZOOM_LEVEL,
                    );
                }),
        );
    }
}
